import Cosmic from 'cosmicjs';

import { IPageFetcher, IPage, IBlock } from '../page';

export type ICosmicConfig = {
    bucket: string;
    readKey?: string;
};

export interface ICosmicImage {
    url: string;
    imgix_url: string;
}

export interface ICosmicImageMetadata {
    image: ICosmicImage;
    mobile_image: ICosmicImage;
    image_alt_text: string;
    title: string;
    subtitle: string;
    cta_text: string;
    cta_url: string;
}

export interface ICosmicFeaturedAd {
    featured_ad: {
        id: string;
        slug?: string;
        title?: string;
        content?: string;
        bucket?: string;
        created_at?: Date;
        created_by?: string;
        modified_at?: Date;
        created?: Date;
        status?: string;
        thumbnail?: string;
        modified_by?: string;
        publish_at?: any;
        published_at: Date;
        unpublish_at?: any;
        type?: string;
        metadata: ICosmicImageMetadata;
    };
}

type ICosmicPageMetadata = {
    featured_ads?: ICosmicFeaturedAd[]
};

export type ICosmicPage = {
    object: {
        title: string;
        slug: string;
        metadata: ICosmicPageMetadata;
    }
};

export class CosmicPageClient implements IPageFetcher {
    private bucket: Cosmic.Bucket;

    public constructor (cosmicConfig: ICosmicConfig) {
        this.bucket = Cosmic().bucket({
            slug: cosmicConfig.bucket,
        });
    }

    public async fetch (id: string): Promise<IPage> {
        try {
            const data: ICosmicPage = await this.bucket.getObject<ICosmicPage>({
                id,
                props: 'slug,title,metadata',
            });

            return CosmicPageClient.transformPage(data);
        } catch (error) {
            throw error;
        }
    }

    private static getOptimizedImage (image: ICosmicImage): string {
        const url: URL = new URL(image.imgix_url);
        const params = new URLSearchParams(url.search);
        const auto: string | null = params.get('auto');
        const fm: string | null = params.get('fm');

        if (!auto) {
            params.append('auto', 'format');
        }

        if (!fm) {
            params.append('fm', 'jpg');
        }

        return `${url.origin}${url.pathname}?${params.toString()}`;
    }

    private static transformPage (data: ICosmicPage): IPage  {
        const blocks: IBlock[] | undefined = data.object.metadata.featured_ads?.map((featureAd: ICosmicFeaturedAd) => {
            const { cta_url, title, subtitle, cta_text, image, mobile_image, image_alt_text } = featureAd.featured_ad.metadata;
            const { published_at  } = featureAd.featured_ad;

            return {
                type: 'FeaturedAd',
                content: {
                    link: cta_url,
                    title,
                    subtitle,
                    cta: cta_text,
                    publishedAt: published_at,
                    fullImage: image.url,
                    image: {
                        src: image.url,
                        alt: image_alt_text,
                        sizes: {
                            '16/9': CosmicPageClient.getOptimizedImage(image),
                            '4/3': CosmicPageClient.getOptimizedImage(mobile_image),
                        },
                    },
                },
            };
        });

        if (!blocks) {
            return {
                blocks: [],
            };
        }

        return {
            blocks,
        };
    }
}
